"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Eye,
  Trash2,
  X,
  Search,
  ChevronDown,
  Loader2,
  Calendar,
  Plus,
} from "lucide-react";
import GlobalLoader from "../components/ui/GlobalLoader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { useToast } from "../hooks/useToast";
import {
  getBookings,
  Booking,
  deleteBooking,
  getBookingById,
  updateBooking,
} from "../services/BookingsApi";
import { getBookingEdit } from "../services/bookings";
import { PageHeader } from "../components/ui/PageHeader";
import { getEmployees } from "../services/employeesApi";
import api from "../services/api";
import BookingDetailsDrawer from "../components/BookingDetailsDrawer";
import BookingEditDrawer from "../components/BookingEditDrawer";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import AppointmentSidebar from "../components/AppointmentSidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import DateCalendar from "../components/DateCalendar";
import { format, parseISO, isValid } from "date-fns";
import { Button } from "../components/ui/Button";
import { cn } from "../lib/utils";
import { useAuth } from "../auth/AuthContext";

type BookingQueryParams = {
  page?: number;
  per_page?: number;
  status?: string;
  customer_id?: number | string;
  employee_id?: number | string;
  date?: string;
  search?: string;
};

interface Customer {
  id: number;
  name: string;
}

export default function BookingsPage() {
  const { user } = useAuth();
  const isClaimed = user?.company?.is_claim === "yes";
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [employees, setEmployees] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState("View All");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("View All");
  const [dateFilter, setDateFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPerPage, setShowPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isAppointmentSidebarOpen, setIsAppointmentSidebarOpen] =
    useState(false);

  // Drawer state
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [viewingBooking, setViewingBooking] = useState<Booking | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toast = useToast();

  // Date filter popover state
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);

  // Global operation state for blocking UI
  const [isUpdating, setIsUpdating] = useState(false);

  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastParamsRef = useRef<string>("");
  const hasInitialLoadRef = useRef(false);

  // Fetch employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const employeesData = await getEmployees();
        console.log("Fetched employees for filter:", employeesData);
        const mappedEmployees = employeesData.map((emp) => ({
          id: emp.id,
          name: emp.name,
        }));
        console.log("Mapped employees for filter:", mappedEmployees);
        setEmployees(mappedEmployees);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const handleAddBooking = () => {
    setIsAppointmentSidebarOpen(true);
  };

  const staffListForSidebar = useMemo(() => {
    return employees.map((emp) => ({
      ...emp,
      color: "#885ABB",
    }));
  }, [employees]);

  // Extract customer list from varying API response shapes
  const normalizeCustomersResponse = (payload: any): Customer[] => {
    const possibleCollections = [
      payload,
      payload?.data,
      payload?.data?.data,
      payload?.data?.data?.data,
    ];

    for (const collection of possibleCollections) {
      if (Array.isArray(collection)) {
        return collection.map((customer: any) => ({
          id: customer.id,
          name: customer.name,
        }));
      }
    }

    return [];
  };

  // Fetch customers for filter dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await api.get("/admin/customers?per_page=1000&page=1");
        const result = response.data;
        const customersData = normalizeCustomersResponse(result);

        if (customersData.length === 0) {
          console.warn("Customers response structure not recognized:", result);
        }

        setCustomers(customersData);
      } catch (err) {
        console.error("Error fetching customers:", err);
        setCustomers([]);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const buildBookingParams = useCallback(
    (overrides: Partial<BookingQueryParams> = {}) => {
      const statusParam =
        statusFilter !== "View All" ? statusFilter : undefined;
      const customerParam =
        customerNameFilter && customers.length > 0
          ? customers.find((c) => c.name === customerNameFilter)?.id
          : undefined;
      const employeeParam =
        employeeFilter !== "View All" && employees.length > 0
          ? employees.find((e) => e.name === employeeFilter)?.id
          : undefined;

      return {
        page: currentPage,
        per_page: showPerPage,
        status: statusParam,
        customer_id: customerParam,
        employee_id: employeeParam,
        date: dateFilter || undefined,
        search: searchQuery || undefined,
        ...overrides,
      };
    },
    [
      statusFilter,
      customerNameFilter,
      employeeFilter,
      customers,
      employees,
      currentPage,
      showPerPage,
      dateFilter,
      searchQuery,
    ],
  );

  // Fetch bookings
  useEffect(() => {
    let active = true;

    const fetchBookingsData = async () => {
      const params = buildBookingParams();
      const paramsKey = JSON.stringify(params);

      // Skip if same params were just used (prevents React.StrictMode double calls)
      if (
        lastParamsRef.current === paramsKey &&
        hasInitialLoadRef.current &&
        !loading
      ) {
        console.log(
          "Skipping duplicate fetch - same params and already loaded/loading",
        );
        return;
      }

      lastParamsRef.current = paramsKey;
      hasInitialLoadRef.current = true;

      setLoading(true);
      setError(null);
      try {
        console.log("Fetching bookings with params:", params);
        const result = await getBookings(params);

        if (!active) return;

        console.log("Bookings result:", result);

        const bookingsList = result.bookings || [];
        setBookings(bookingsList);
        setTotal(result.total || bookingsList.length || 0);
        setTotalPages(result.lastPage || 1);

        if (bookingsList.length === 0) {
          setDebugInfo("");
        } else {
          setDebugInfo(
            `Loaded ${bookingsList.length} booking(s) out of ${
              result.total || 0
            } total`,
          );
        }
      } catch (err: any) {
        if (!active) return;
        console.error("Error fetching bookings:", err);
        console.error("Error response:", err.response?.data);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to load bookings. Please check the console for details.";
        setError(errorMessage);
        setBookings([]);
        setTotal(0);
        setTotalPages(1);
        toast.error("Failed to load bookings");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchBookingsData();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    customerNameFilter,
    employeeFilter,
    dateFilter,
    searchQuery,
    currentPage,
    showPerPage,
    customersLoading,
    employeesLoading,
  ]);

  const handleReset = () => {
    setStatusFilter("View All");
    setCustomerNameFilter("");
    setCustomerSearchTerm("");
    setIsCustomerDropdownOpen(false);
    setEmployeeFilter("View All");
    setDateFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleEdit = async (booking: Booking, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditDrawerOpen(true);
    setEditLoading(true);
    setEditingBooking(booking);
    try {
      const freshBooking = await getBookingById(booking.id);
      if (freshBooking) {
        setEditingBooking(freshBooking);
      }
    } catch (err: any) {
      console.error("Error fetching booking details for edit:", err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleView = async (booking: Booking, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsDrawerOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setViewingBooking(booking);
    try {
      // Fetch both endpoints in parallel: /admin/bookings/{id} for general data
      // and /admin/bookings/{id}/edit for per-item employee_id which the list API omits
      const [freshBooking, editData] = await Promise.all([
        getBookingById(booking.id),
        getBookingEdit(booking.id).catch(() => null),
      ]);

      if (freshBooking) {
        // Merge per-item employee_id from the edit endpoint into rawData.items
        if (editData?.booking?.items && freshBooking.rawData?.items) {
          freshBooking.rawData.items = freshBooking.rawData.items.map(
            (item: any, idx: number) => {
              const editItem = editData.booking.items[idx];
              if (!editItem) return item;
              return {
                ...item,
                employee_id: item.employee_id ?? editItem.employee_id ?? null,
                employee: item.employee ?? editItem.employee ?? null,
              };
            }
          );
        }
        setViewingBooking(freshBooking);
      } else {
        setDetailsError("Unable to load booking details.");
      }
    } catch (err: any) {
      console.error("Error fetching booking details:", err);
      setDetailsError(
        err.response?.data?.message || "Failed to load booking details.",
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = (bookingId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setBookingToDelete(bookingId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!bookingToDelete) return;

    setIsDeleting(true);
    try {
      await deleteBooking(bookingToDelete);
      const result = await getBookings(buildBookingParams());
      setBookings(result.bookings || []);
      setTotal(result.total || result.bookings?.length || 0);
      setTotalPages(result.lastPage || 1);

      if (viewingBooking?.id === bookingToDelete) {
        setViewingBooking(null);
        setIsDetailsDrawerOpen(false);
      }
      toast.success("Booking deleted successfully");
    } catch (err: any) {
      console.error("Error deleting booking:", err);
      toast.error(err.response?.data?.message || "Failed to delete booking");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setBookingToDelete(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers;
    const lower = customerSearchTerm.toLowerCase();
    return customers.filter((customer) =>
      customer.name.toLowerCase().includes(lower),
    );
  }, [customers, customerSearchTerm]);

  const customerDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };
    if (isCustomerDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCustomerDropdownOpen]);

  // Client-side filtering for search
  const filteredBookings = useMemo(() => {
    if (!searchQuery) return bookings;

    const lower = searchQuery.toLowerCase();
    return bookings.filter((booking) => {
      return (
        booking.customerName.toLowerCase().includes(lower) ||
        (booking.employeeName &&
          booking.employeeName !== "Unknown Employee" &&
          booking.employeeName.toLowerCase().includes(lower)) ||
        booking.id.toString().includes(lower)
      );
    });
  }, [bookings, searchQuery]);

  return (
    <>
      {/* Global Loader for Delete/Update Operations */}
      {(isDeleting || isUpdating) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <GlobalLoader
            initialPageLoad={false}
            compensateSidebar={false}
            description={
              isDeleting ? "Deleting booking..." : "Updating booking..."
            }
          />
        </div>
      )}

      <div className="w-full min-h-screen pt-4">
        <PageHeader
          title="Bookings"
          description="View and manage all your scheduled appointments"
          icon={Calendar}
          variant="default"
          className="mb-0"
          actions={
            isClaimed ? (
              <button
                onClick={handleAddBooking}
                className="flex items-center justify-center bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg px-4 lg:px-6 py-2.5 lg:py-3 text-sm lg:text-[16px] font-semibold transition-colors w-full sm:w-auto shadow-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden xs:inline">Add Booking</span>
                <span className="xs:hidden">Add</span>
              </button>
            ) : undefined
          }
        />

        {/* Filters Section */}
        <div className="py-2">
          <div className="bg-white border border-gray-200 shadow-sm mb-3 p-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-4 w-full">
                {/* Status Filter */}
                <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
                  <label className="text-xs font-medium text-gray-500 ml-1">
                    Status
                  </label>
                  <Select
                    value={statusFilter}
                    onValueChange={(value) => {
                      setLoading(true);
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full bg-white border-gray-200 focus:ring-2 focus:ring-[#885ABB]">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="View All">View All</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Booked">Booked</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="No Show">No Show</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Customer Name Filter */}
                <div
                  className="flex flex-col gap-1.5 flex-1 min-w-[200px]"
                  ref={customerDropdownRef}
                >
                  <label className="text-xs font-medium text-gray-500 ml-1">
                    Customer
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsCustomerDropdownOpen((prev) => !prev)}
                      disabled={customersLoading}
                      className="flex items-center justify-between w-full h-10 px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#885ABB] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="truncate">
                        {customersLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#885ABB] border-t-transparent" />
                            <span>Loading...</span>
                          </div>
                        ) : (
                          customerNameFilter || "Select Customer"
                        )}
                      </span>
                      {!customersLoading && (
                        <ChevronDown
                          className={`w-4 h-4 text-[#885ABB] transition-transform ${
                            isCustomerDropdownOpen ? "rotate-180" : ""
                          }`}
                          strokeWidth={4}
                        />
                      )}
                    </button>
                    {isCustomerDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search customers"
                              value={customerSearchTerm}
                              onChange={(e) =>
                                setCustomerSearchTerm(e.target.value)
                              }
                              className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                              customerNameFilter === ""
                                ? "text-[#885ABB]"
                                : "text-gray-700"
                            }`}
                            onClick={() => {
                              setLoading(true);
                              setCustomerNameFilter("");
                              setCustomerSearchTerm("");
                              setCurrentPage(1);
                              setIsCustomerDropdownOpen(false);
                            }}
                          >
                            View All Customers
                          </button>
                          {filteredCustomers.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              No matches found
                            </div>
                          ) : (
                            filteredCustomers.map((customer) => (
                              <button
                                key={customer.id}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                  customerNameFilter === customer.name
                                    ? "text-[#885ABB]"
                                    : "text-gray-700"
                                }`}
                                onClick={() => {
                                  setLoading(true);
                                  setCustomerNameFilter(customer.name);
                                  setCurrentPage(1);
                                  setIsCustomerDropdownOpen(false);
                                }}
                              >
                                {customer.name}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Employee Filter */}
                <div className="flex flex-col gap-1.5 flex-1 min-w-[180px]">
                  <label className="text-xs font-medium text-gray-500 ml-1">
                    Employee
                  </label>
                  <Select
                    value={employeeFilter}
                    onValueChange={(value) => {
                      setLoading(true);
                      setEmployeeFilter(value);
                      setCurrentPage(1);
                    }}
                    disabled={employeesLoading}
                  >
                    <SelectTrigger className="w-full bg-white border-gray-200 focus:ring-2 focus:ring-[#885ABB]">
                      {employeesLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#885ABB] border-t-transparent" />
                          <span>Loading...</span>
                        </div>
                      ) : (
                        <SelectValue placeholder="Select Employee" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="View All">View All</SelectItem>
                      {employees.map((employee) => (
                        <SelectItem
                          key={employee.id}
                          value={employee.name || `Employee ${employee.id}`}
                        >
                          {employee.name || `Employee ${employee.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Filter */}
                <div className="flex flex-col gap-1.5 flex-1 min-w-[180px]">
                  <label className="text-xs font-medium text-gray-500 ml-1">
                    Date
                  </label>
                  <Popover
                    open={isDatePopoverOpen}
                    onOpenChange={setIsDatePopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-10 justify-between text-left font-normal border-gray-200 bg-white hover:bg-white focus:ring-2 focus:ring-[#885ABB]",
                          !dateFilter && "text-muted-foreground",
                        )}
                      >
                        <span>
                          {dateFilter && isValid(parseISO(dateFilter))
                            ? format(parseISO(dateFilter), "dd/MM/yyyy")
                            : "Select date"}
                        </span>
                        <Calendar
                          className="h-4 w-4 text-[#885ABB]"
                          strokeWidth={4}
                        />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <DateCalendar
                        selectedDate={
                          dateFilter && isValid(parseISO(dateFilter))
                            ? parseISO(dateFilter)
                            : new Date()
                        }
                        onSelectDate={(date) => {
                          setLoading(true);
                          setDateFilter(format(date, "yyyy-MM-dd"));
                          setCurrentPage(1);
                          setIsDatePopoverOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Reset Button */}
                <div className="flex items-end lg:h-10 mt-auto">
                  <Button
                    onClick={handleReset}
                    className="w-full lg:w-auto bg-purple-100 text-[#885ABB] hover:bg-purple-200 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors h-10"
                    variant="ghost"
                  >
                    <X className="w-4 h-4" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>

            {/* Search and Show Per Page */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-medium">Show</span>
                <Select
                  value={showPerPage.toString()}
                  onValueChange={(value) => {
                    setShowPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px] bg-white border-gray-200">
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500 font-medium">
                  per page
                </span>
              </div>

              <div className="flex items-center gap-3">
                {/* Search Bar */}
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search appointments..."
                    value={searchQuery}
                    onChange={(e) => {
                      setLoading(true);
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 bg-white border-gray-200 focus:ring-2 focus:ring-[#885ABB]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile & Tablet Card View */}
          <div className="lg:hidden space-y-4 mb-6">
            {loading ? (
              <div className="h-96 relative bg-white border border-gray-200 rounded-xl">
                <GlobalLoader
                  initialPageLoad={false}
                  compensateSidebar={false}
                  description="Loading bookings..."
                />
              </div>
            ) : error ? (
              <div className="p-8 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex flex-col items-center gap-2">
                  <span className="text-red-500 font-medium">{error}</span>
                  <span className="text-sm text-gray-400">
                    Please check the browser console for details.
                  </span>
                </div>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                  <Calendar
                    className="w-8 h-8 text-gray-300"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-600">
                  No bookings found
                </h3>
                <p className="text-sm text-gray-400 max-w-sm mx-auto">
                  We couldn&apos;t find any bookings matching your current
                  filters.
                </p>
              </div>
            ) : (
              filteredBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-gray-900 text-[15px]">
                        {booking.customerName}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {booking.employeeName &&
                        booking.employeeName !== "Unknown Employee"
                          ? booking.employeeName
                          : "No Staff Assigned"}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        booking.status === "Completed"
                          ? "bg-green-100 text-green-700"
                          : booking.status === "Pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : booking.status === "Cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>

                  <div className="text-xs text-gray-600 flex items-center gap-2 py-1">
                    <span className="font-semibold uppercase text-[10px] text-gray-400">
                      Date:
                    </span>
                    <span className="font-medium text-gray-800">
                      {booking.bookingTime}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 border-t border-b border-gray-100 py-3 mt-1">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">
                        Total
                      </p>
                      <p className="font-bold text-gray-900">
                        {currencySymbol}
                        {booking.total.toFixed(2)}
                      </p>
                    </div>

                    <div className="text-center border-l border-gray-100">
                      <p className="text-[10px] text-green-500 uppercase font-bold mb-1">
                        Paid
                      </p>
                      <p className="font-bold text-green-600">
                        {currencySymbol}
                        {booking.paid.toFixed(2)}
                      </p>
                    </div>

                    <div className="text-center border-l border-gray-100">
                      <p className="text-[10px] text-red-500 uppercase font-bold mb-1">
                        Due
                      </p>
                      <p className="font-bold text-red-600">
                        {currencySymbol}
                        {booking.pending.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    {booking.status !== "Completed" && (
                      <button
                        onClick={(e) => handleEdit(booking, e)}
                        className="p-2 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-lg transition-colors shadow-sm"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleView(booking, e)}
                      className="p-2 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-lg transition-colors shadow-sm"
                      title="View"
                    >
                      <Eye className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(booking.id, e)}
                      className="p-2 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-lg transition-colors shadow-sm"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Mobile Pagination */}
            {!loading && !error && totalPages > 1 && (
              <div className="flex flex-col gap-3 mt-6 mb-8 px-1">
                <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-gray-400">
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <span>{total} Total Bookings</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    variant="outline"
                    className="h-11 rounded-xl border-gray-200 text-gray-700 bg-white font-bold"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="h-11 rounded-xl border-gray-200 text-gray-700 bg-white font-bold"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Table Header - Purple background */}
                <thead className="bg-[#885ABB] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">#</th>
                    <th className="px-4 py-3 text-left font-semibold">
                      Customer Name
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">
                      Employee Name
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">
                      Booking Time
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">Total</th>
                    <th className="px-4 py-3 text-left font-semibold">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="h-96 relative">
                        <GlobalLoader
                          initialPageLoad={false}
                          compensateSidebar={false}
                          description="Loading bookings..."
                        />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-red-500 font-medium">
                            {error}
                          </span>
                          <span className="text-sm text-gray-500">
                            Please check the browser console for more details.
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking, index) => {
                      const rowNumber =
                        (currentPage - 1) * showPerPage + index + 1;
                      return (
                        <tr
                          key={booking.id}
                          className={`${
                            index % 2 === 0 ? "bg-[#FCFCFD]" : "bg-white"
                          } hover:bg-gray-50 border-t border-gray-200 transition-colors`}
                        >
                          <td className="px-4 py-3 text-gray-700 font-medium">
                            {rowNumber}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {booking.customerName}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {booking.employeeName &&
                            booking.employeeName !== "Unknown Employee"
                              ? booking.employeeName
                              : ""}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {booking.bookingTime}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="text-gray-700">
                                Total ={currencySymbol}
                                {booking.total.toFixed(2)}
                              </span>
                              <span className="text-green-600 text-xs">
                                Paid ={currencySymbol}
                                {booking.paid.toFixed(2)}
                              </span>
                              <span className="text-red-600 text-xs">
                                Pending ={currencySymbol}
                                {booking.pending.toFixed(2)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 border border-blue-200">
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {booking.status !== "Completed" && (
                                <button
                                  onClick={(e) => handleEdit(booking, e)}
                                  className="p-2 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-md transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={(e) => handleView(booking, e)}
                                className="p-2 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-md transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => handleDelete(booking.id, e)}
                                className="p-2 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-md transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                  {!loading && !error && filteredBookings.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-16 text-center text-gray-400"
                      >
                        <div className="flex flex-col items-center justify-center gap-3">
                          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                            <Calendar
                              className="w-8 h-8 text-gray-300"
                              strokeWidth={1.5}
                            />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-600">
                            No bookings found
                          </h3>
                          <p className="text-sm text-gray-400 max-w-sm mx-auto">
                            We couldn&apos;t find any bookings matching your
                            current filters. Try adjusting your search or
                            filters.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Desktop Pagination */}
            {!loading && !error && totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * showPerPage + 1} to{" "}
                  {Math.min(currentPage * showPerPage, total)} of {total}{" "}
                  bookings
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <BookingDetailsDrawer
        isOpen={isDetailsDrawerOpen}
        booking={viewingBooking}
        loading={detailsLoading}
        error={detailsError}
        onClose={() => {
          setIsDetailsDrawerOpen(false);
          setViewingBooking(null);
        }}
        onDelete={(id: number) => handleDelete(id)}
        onEdit={(id: number) => {
          const bookingToEdit =
            bookings.find((b) => b.id === id) || viewingBooking || null;
          if (bookingToEdit) {
            handleEdit(bookingToEdit);
          }
        }}
      />

      <BookingEditDrawer
        isOpen={isEditDrawerOpen}
        booking={editingBooking}
        loading={editLoading}
        employees={employees}
        onClose={() => setIsEditDrawerOpen(false)}
        onSubmit={async (payload: any) => {
          if (!editingBooking) return;

          setIsUpdating(true);
          try {
            // Format date as Y-m-d
            const bookingDate = payload.bookingDate;

            // Format time as "H:i a" (e.g., "02:21 AM")
            const [hours, minutes] = payload.bookingTime.split(":");
            const hour24 = parseInt(hours, 10);
            const hour12 = hour24 % 12 || 12;
            const ampm = hour24 >= 12 ? "PM" : "AM";
            const hiddenBookingTime = `${String(hour12).padStart(
              2,
              "0",
            )}:${minutes} ${ampm}`;

            // Separate services and products
            const services = payload.lineItems.filter(
              (item: any) => item.type === "service",
            );
            const products = payload.lineItems.filter(
              (item: any) => item.type === "product",
            );

            // Build service arrays (item_ids, types, cart_quantity, item_prices)
            const itemIds: (string | number)[] = [];
            const types: string[] = [];
            const cartQuantity: number[] = [];
            const itemPrices: number[] = [];

            services.forEach((item: any) => {
              const serviceId =
                item.serviceId || item.id.replace("service-", "").split("-")[0];
              itemIds.push(serviceId);
              types.push("service"); // Backend expects 'service' or 'deal', we only have services
              cartQuantity.push(item.quantity);
              itemPrices.push(item.unitPrice);
            });

            // Build product arrays (cart_products, product_quantity, product_prices)
            const cartProducts: (string | number)[] = [];
            const productQuantity: number[] = [];
            const productPrices: number[] = [];

            products.forEach((item: any) => {
              const productId =
                item.productId || item.id.replace("product-", "").split("-")[0];
              cartProducts.push(productId);
              productQuantity.push(item.quantity);
              productPrices.push(item.unitPrice);
            });

            // Determine payment status based on booking status
            const paymentStatus =
              payload.status === "Completed" ? "completed" : "pending";

            // Build API payload
            const apiPayload: any = {
              booking_date: bookingDate,
              hidden_booking_time: hiddenBookingTime,
              status: payload.status,
              payment_status: paymentStatus,
              cart_discount: 0, // No discount field in drawer currently
            };

            // Add service arrays if services exist
            if (itemIds.length > 0) {
              apiPayload.item_ids = itemIds;
              apiPayload.types = types;
              apiPayload.cart_quantity = cartQuantity;
              apiPayload.item_prices = itemPrices;
            }

            // Add product arrays if products exist
            if (cartProducts.length > 0) {
              apiPayload.cart_products = cartProducts;
              apiPayload.product_quantity = productQuantity;
              apiPayload.product_prices = productPrices;
            }

            // Add employee_id as array if provided
            if (payload.employeeId) {
              apiPayload.employee_id = [payload.employeeId];
            }

            // Call API
            await updateBooking(editingBooking.id, apiPayload);

            // Refresh list
            const result = await getBookings(buildBookingParams());
            setBookings(result.bookings || []);
            setTotal(result.total || result.bookings?.length || 0);
            setTotalPages(result.lastPage || 1);

            setIsEditDrawerOpen(false);
            setEditingBooking(null);
            toast.success("Booking updated successfully");
          } catch (err: any) {
            console.error("Error updating booking:", err);
            toast.error(
              err.response?.data?.message || "Failed to update booking",
            );
          } finally {
            setIsUpdating(false);
          }
        }}
        onDelete={(id) => {
          setIsEditDrawerOpen(false);
          handleDelete(id);
        }}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking? This action cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        variant="danger"
      />

      <AppointmentSidebar
        isOpen={isAppointmentSidebarOpen}
        onClose={() => setIsAppointmentSidebarOpen(false)}
        staffList={staffListForSidebar}
        initialServiceType="Services"
        mode="dashboard"
      />
    </>
  );
}
